import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { deriveKeys, SessionKeys, wrapSecret } from './web-crypto';

interface PublicKeyInfo {
  mode: 'off' | 'optional' | 'required';
  kid?: string;
  publicKey?: string;
  /** Capacidad: el gateway comprime las respuestas antes de cifrarlas. */
  z?: boolean;
}

/**
 * Sesión de cifrado con el gateway. El gateway decide si el cifrado está activo (ENC_ENABLED en su .env) y lo anuncia en `public-key`:
 * con `mode: "off"` no se hace nada y las peticiones siguen en claro. La clave de sesión es efímera, vive solo en memoria y se rehace en cada carga.
 */
@Injectable({ providedIn: 'root' })
export class CryptoSessionService {
  private session: SessionKeys | null = null;
  private pending: Promise<SessionKeys | null> | null = null;
  private off = false;

  /** La sesión vigente, abriéndola si hace falta. `null`: el cifrado está apagado o no se pudo abrir (la petición sale en claro). */
  ensure(): Promise<SessionKeys | null> {
    if (this.off) return Promise.resolve(null);
    if (this.session) return Promise.resolve(this.session);
    return (this.pending ??= this.open().finally(() => (this.pending = null)));
  }

  /** El gateway dijo que la sesión venció: la próxima petición abre una nueva. */
  reset(): void {
    this.session = null;
  }

  private async open(): Promise<SessionKeys | null> {
    try {
      // fetch y no HttpClient: estas dos llamadas no pueden pasar por el interceptor de cifrado.
      const headers = { 'x-system-identifier': environment.systemIdentifier || '' };
      const infoRes = await fetch(`${environment.apiUrl}/crypto/public-key`, { headers, cache: 'no-store' });
      if (!infoRes.ok) return null;
      const info = (await infoRes.json()) as PublicKeyInfo;
      if (info.mode === 'off' || !info.kid || !info.publicKey) {
        this.off = info.mode === 'off';
        return null;
      }
      const secret = crypto.getRandomValues(new Uint8Array(32));
      const res = await fetch(`${environment.apiUrl}/crypto/session`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kid: info.kid, wrappedKey: await wrapSecret(info.publicKey, secret) }),
      });
      if (!res.ok) return null;
      const { sid } = (await res.json()) as { sid: string };
      return (this.session = { ...(await deriveKeys(secret, sid)), z: info.z === true });
    } catch {
      return null;
    }
  }
}

